import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  UserPermissionsService,
  UserPermissions,
} from "../services/common/user-permissions.service";
import { GroupNames, GroupName } from "../utils/group-names";

/**
 * Structural directive for conditional rendering based on group membership
 *
 * Usage examples:
 * - *appIfInGroup="GroupNames.TURBINE_USERS"
 * - *appIfInGroup="[GroupNames.TECHNICAL_LETTER_VIEWERS, GroupNames.TECHNICAL_LETTER_ADMINS]"
 * - *appIfInGroup="GroupNames.RAM_PLANNERS; requireAll: true"
 */
@Directive({
    selector: "[appIfInGroup]",
    standalone: false
})
export class IfInGroupDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private hasRendered = false;

  @Input() set appIfInGroup(groups: GroupName | GroupName[]) {
    this.requiredGroups = Array.isArray(groups) ? groups : [groups];
    this.updateView();
  }

  @Input() requireAll = false; // If true, user must be in ALL groups; if false, ANY group

  private requiredGroups: GroupName[] = [];

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private userPermissionsService: UserPermissionsService
  ) {}

  ngOnInit(): void {
    this.userPermissionsService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        this.updateView(permissions);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(permissions?: UserPermissions | null): void {
    if (!permissions || this.requiredGroups.length === 0) {
      this.hideView();
      return;
    }

    const hasAccess = this.checkGroupAccess(permissions);

    if (hasAccess && !this.hasRendered) {
      this.showView();
    } else if (!hasAccess && this.hasRendered) {
      this.hideView();
    }
  }
  private checkGroupAccess(permissions: UserPermissions): boolean {
    const groupChecks = this.requiredGroups.map((group) => {
      switch (group) {
        case GroupNames.TURBINE_USERS:
          return permissions.isTurbineUser;
        case GroupNames.RAM_PLANNERS:
          return permissions.isRamPlanner;
        case GroupNames.TECHNICAL_LETTER_VIEWERS:
          return permissions.isTechnicalLetterViewer;
        case GroupNames.TECHNICAL_LETTER_ADMINS:
          return permissions.isTechnicalLetterAdmin;
        default:
          return false;
      }
    });

    return this.requireAll
      ? groupChecks.every((check) => check === true) // ALL groups required
      : groupChecks.some((check) => check === true); // ANY group sufficient
  }

  private showView(): void {
    if (!this.hasRendered) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasRendered = true;
    }
  }

  private hideView(): void {
    if (this.hasRendered) {
      this.viewContainer.clear();
      this.hasRendered = false;
    }
  }
}
